import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["defaultIndex", "onChange", "index", "isManual", "isLazy", "lazyBehavior", "orientation", "direction"],
  _excluded2 = ["isDisabled", "isFocusable"],
  _excluded3 = ["children"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
// src/use-tabs.ts
import { useClickable } from "@chakra-ui/clickable";
import { createDescendantContext } from "@chakra-ui/descendant";
import { createContext } from "@chakra-ui/react-context";
import { useSafeLayoutEffect } from "@chakra-ui/react-use-safe-layout-effect";
import { useControllableState } from "@chakra-ui/react-use-controllable-state";
import { getValidChildren } from "@chakra-ui/react-children-utils";
import { mergeRefs } from "@chakra-ui/react-use-merge-refs";
import { lazyDisclosure } from "@chakra-ui/lazy-utils";
import { callAllHandlers } from "@chakra-ui/shared-utils";
import { useCallback, useEffect, useRef, useState, useId, createElement } from "react";
var _createDescendantCont = createDescendantContext(),
  _createDescendantCont2 = _slicedToArray(_createDescendantCont, 4),
  TabsDescendantsProvider = _createDescendantCont2[0],
  useTabsDescendantsContext = _createDescendantCont2[1],
  useTabsDescendants = _createDescendantCont2[2],
  useTabsDescendant = _createDescendantCont2[3];
function useTabs(props) {
  var _a;
  var defaultIndex = props.defaultIndex,
    onChange = props.onChange,
    index = props.index,
    isManual = props.isManual,
    isLazy = props.isLazy,
    _props$lazyBehavior = props.lazyBehavior,
    lazyBehavior = _props$lazyBehavior === void 0 ? "unmount" : _props$lazyBehavior,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? "horizontal" : _props$orientation,
    _props$direction = props.direction,
    direction = _props$direction === void 0 ? "ltr" : _props$direction,
    htmlProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(defaultIndex != null ? defaultIndex : 0),
    _useState2 = _slicedToArray(_useState, 2),
    focusedIndex = _useState2[0],
    setFocusedIndex = _useState2[1];
  var _useControllableState = useControllableState({
      defaultValue: defaultIndex != null ? defaultIndex : 0,
      value: index,
      onChange: onChange
    }),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    selectedIndex = _useControllableState2[0],
    setSelectedIndex = _useControllableState2[1];
  useEffect(function () {
    if (index != null) {
      setFocusedIndex(index);
    }
  }, [index]);
  var descendants = useTabsDescendants();
  var uuid = useId();
  var uid = (_a = props.id) != null ? _a : uuid;
  var id = "tabs-".concat(uid);
  return {
    id: id,
    selectedIndex: selectedIndex,
    focusedIndex: focusedIndex,
    setSelectedIndex: setSelectedIndex,
    setFocusedIndex: setFocusedIndex,
    isManual: isManual,
    isLazy: isLazy,
    lazyBehavior: lazyBehavior,
    orientation: orientation,
    descendants: descendants,
    direction: direction,
    htmlProps: htmlProps
  };
}
var _createContext = createContext({
    name: "TabsContext",
    errorMessage: "useTabsContext: `context` is undefined. Seems you forgot to wrap all tabs components within <Tabs />"
  }),
  _createContext2 = _slicedToArray(_createContext, 2),
  TabsProvider = _createContext2[0],
  useTabsContext = _createContext2[1];
function useTabList(props) {
  var _useTabsContext = useTabsContext(),
    focusedIndex = _useTabsContext.focusedIndex,
    orientation = _useTabsContext.orientation,
    direction = _useTabsContext.direction;
  var descendants = useTabsDescendantsContext();
  var onKeyDown = useCallback(function (event) {
    var _keyMap;
    var nextTab = function nextTab() {
      var _a;
      var next = descendants.nextEnabled(focusedIndex);
      if (next) (_a = next.node) == null ? void 0 : _a.focus();
    };
    var prevTab = function prevTab() {
      var _a;
      var prev = descendants.prevEnabled(focusedIndex);
      if (prev) (_a = prev.node) == null ? void 0 : _a.focus();
    };
    var firstTab = function firstTab() {
      var _a;
      var first = descendants.firstEnabled();
      if (first) (_a = first.node) == null ? void 0 : _a.focus();
    };
    var lastTab = function lastTab() {
      var _a;
      var last = descendants.lastEnabled();
      if (last) (_a = last.node) == null ? void 0 : _a.focus();
    };
    var isHorizontal = orientation === "horizontal";
    var isVertical = orientation === "vertical";
    var eventKey = event.key;
    var ArrowStart = direction === "ltr" ? "ArrowLeft" : "ArrowRight";
    var ArrowEnd = direction === "ltr" ? "ArrowRight" : "ArrowLeft";
    var keyMap = (_keyMap = {}, _defineProperty(_keyMap, ArrowStart, function () {
      return isHorizontal && prevTab();
    }), _defineProperty(_keyMap, ArrowEnd, function () {
      return isHorizontal && nextTab();
    }), _defineProperty(_keyMap, "ArrowDown", function ArrowDown() {
      return isVertical && nextTab();
    }), _defineProperty(_keyMap, "ArrowUp", function ArrowUp() {
      return isVertical && prevTab();
    }), _defineProperty(_keyMap, "Home", firstTab), _defineProperty(_keyMap, "End", lastTab), _keyMap);
    var action = keyMap[eventKey];
    if (action) {
      event.preventDefault();
      action(event);
    }
  }, [descendants, focusedIndex, orientation, direction]);
  return _objectSpread(_objectSpread({}, props), {}, {
    role: "tablist",
    "aria-orientation": orientation,
    onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown)
  });
}
function useTab(props) {
  var isDisabled = props.isDisabled,
    isFocusable = props.isFocusable,
    htmlProps = _objectWithoutProperties(props, _excluded2);
  var _useTabsContext2 = useTabsContext(),
    setSelectedIndex = _useTabsContext2.setSelectedIndex,
    isManual = _useTabsContext2.isManual,
    id = _useTabsContext2.id,
    setFocusedIndex = _useTabsContext2.setFocusedIndex,
    selectedIndex = _useTabsContext2.selectedIndex;
  var _useTabsDescendant = useTabsDescendant({
      disabled: isDisabled && !isFocusable
    }),
    index = _useTabsDescendant.index,
    register = _useTabsDescendant.register;
  var isSelected = index === selectedIndex;
  var onClick = function onClick() {
    setSelectedIndex(index);
  };
  var onFocus = function onFocus() {
    setFocusedIndex(index);
    var isDisabledButFocusable = isDisabled && isFocusable;
    var shouldSelect = !isManual && !isDisabledButFocusable;
    if (shouldSelect) {
      setSelectedIndex(index);
    }
  };
  var clickableProps = useClickable(_objectSpread(_objectSpread({}, htmlProps), {}, {
    ref: mergeRefs(register, props.ref),
    isDisabled: isDisabled,
    isFocusable: isFocusable,
    onClick: callAllHandlers(props.onClick, onClick)
  }));
  var type = "button";
  return _objectSpread(_objectSpread({}, clickableProps), {}, {
    id: makeTabId(id, index),
    role: "tab",
    tabIndex: isSelected ? 0 : -1,
    type: type,
    "aria-selected": isSelected,
    "aria-controls": makeTabPanelId(id, index),
    onFocus: isDisabled ? void 0 : callAllHandlers(props.onFocus, onFocus)
  });
}
var _createContext3 = createContext({}),
  _createContext4 = _slicedToArray(_createContext3, 2),
  TabPanelProvider = _createContext4[0],
  useTabPanelContext = _createContext4[1];
function useTabPanels(props) {
  var context = useTabsContext();
  var id = context.id,
    selectedIndex = context.selectedIndex;
  var validChildren = getValidChildren(props.children);
  var children = validChildren.map(function (child, index) {
    return createElement(TabPanelProvider, {
      key: index,
      value: {
        isSelected: index === selectedIndex,
        id: makeTabPanelId(id, index),
        tabId: makeTabId(id, index),
        selectedIndex: selectedIndex
      }
    }, child);
  });
  return _objectSpread(_objectSpread({}, props), {}, {
    children: children
  });
}
function useTabPanel(props) {
  var children = props.children,
    htmlProps = _objectWithoutProperties(props, _excluded3);
  var _useTabsContext3 = useTabsContext(),
    isLazy = _useTabsContext3.isLazy,
    lazyBehavior = _useTabsContext3.lazyBehavior;
  var _useTabPanelContext = useTabPanelContext(),
    isSelected = _useTabPanelContext.isSelected,
    id = _useTabPanelContext.id,
    tabId = _useTabPanelContext.tabId;
  var hasBeenSelected = useRef(false);
  if (isSelected) {
    hasBeenSelected.current = true;
  }
  var shouldRenderChildren = lazyDisclosure({
    wasSelected: hasBeenSelected.current,
    isSelected: isSelected,
    enabled: isLazy,
    mode: lazyBehavior
  });
  return _objectSpread(_objectSpread({
    tabIndex: 0
  }, htmlProps), {}, {
    children: shouldRenderChildren ? children : null,
    role: "tabpanel",
    "aria-labelledby": tabId,
    hidden: !isSelected,
    id: id
  });
}
function useTabIndicator() {
  var context = useTabsContext();
  var descendants = useTabsDescendantsContext();
  var selectedIndex = context.selectedIndex,
    orientation = context.orientation;
  var isHorizontal = orientation === "horizontal";
  var isVertical = orientation === "vertical";
  var _useState3 = useState(function () {
      if (isHorizontal) return {
        left: 0,
        width: 0
      };
      if (isVertical) return {
        top: 0,
        height: 0
      };
      return void 0;
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    rect = _useState4[0],
    setRect = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    hasMeasured = _useState6[0],
    setHasMeasured = _useState6[1];
  useSafeLayoutEffect(function () {
    if (selectedIndex == null) return;
    var tab = descendants.item(selectedIndex);
    if (tab == null) return;
    if (isHorizontal) {
      setRect({
        left: tab.node.offsetLeft,
        width: tab.node.offsetWidth
      });
    }
    if (isVertical) {
      setRect({
        top: tab.node.offsetTop,
        height: tab.node.offsetHeight
      });
    }
    var id = requestAnimationFrame(function () {
      setHasMeasured(true);
    });
    return function () {
      if (id) {
        cancelAnimationFrame(id);
      }
    };
  }, [selectedIndex, isHorizontal, isVertical, descendants]);
  return _objectSpread({
    position: "absolute",
    transitionProperty: "left, right, top, bottom, height, width",
    transitionDuration: hasMeasured ? "200ms" : "0ms",
    transitionTimingFunction: "cubic-bezier(0, 0, 0.2, 1)"
  }, rect);
}
function makeTabId(id, index) {
  return "".concat(id, "--tab-").concat(index);
}
function makeTabPanelId(id, index) {
  return "".concat(id, "--tabpanel-").concat(index);
}
export { TabsDescendantsProvider, useTabsDescendantsContext, useTabsDescendants, useTabsDescendant, useTabs, TabsProvider, useTabsContext, useTabList, useTab, useTabPanels, useTabPanel, useTabIndicator };